import {addCircularTabFocus, focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {ArrowLeftSmall, ArrowRightSmall} from '@wix/wix-ui-icons-common'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import React, {useEffect} from 'react'
import {HoverStyles, Text, TextButton} from 'wix-ui-tpa/cssVars'
import {useAppActions, useAppSelector} from '../../../../hooks/runtime'
import {isChooseMultipleSeatsMode, isChooseSeatMode} from '../../../../selectors/navigation'
import {isInAccessibilityMode, isInDescription, isInMobileBasket} from '../../../../selectors/seating/mode'
import {getFilteredPlacesCount} from '../../../../selectors/seating/places'
import {SlideInModal} from '../../slide-in-modal'
import {classes} from './accessibility-mode.st.css'
import {Filters} from './filters'
import {Footer} from './footer'
import {Header} from './header'
import {AccessibilityModeProps} from './interfaces'
import {TicketsCountInfo} from './tickets-count-info'
import {TicketsList} from './tickets-list'

export const AccessibilityMode = ({
  onFooterButtonClick,
  setForcedPopoverPlanPlaceId,
  setAccessibilityToast,
  returnFocusElement,
}: AccessibilityModeProps) => {
  const {isMobile, isRTL} = useEnvironment()
  const {t} = useTranslation()
  const {setShowAccessibilityMode} = useAppActions()

  const showAccessibilityMode = useAppSelector(isInAccessibilityMode)
  const placesCount = useAppSelector(getFilteredPlacesCount)
  const chooseSeatMode = useAppSelector(isChooseSeatMode)
  const chooseMultipleSeatsMode = useAppSelector(isChooseMultipleSeatsMode)
  const inDescription = useAppSelector(isInDescription)
  const inBasket = useAppSelector(isInMobileBasket)

  const onBackClick = () => {
    setAccessibilityToast?.(false)
    setShowAccessibilityMode(false)
  }

  useEffect(() => {
    if (isMobile) {
      focusElement({selector: hookToAttributeSelector(DH.BACK_TO_MAP), canFocus: true})
    }

    return () => (returnFocusElement ? focusElement({selector: returnFocusElement, canFocus: true}) : null)
  }, [])

  useEffect(() => {
    if (!inBasket && !inDescription && isMobile) {
      addCircularTabFocus(hookToAttributeSelector(DH.BACK_TO_MAP), hookToAttributeSelector(DH.VIEW_BASKET))
    }
  }, [inBasket, inDescription, isMobile])

  if (isMobile) {
    return (
      <>
        <div className={classes.mobileContainer} data-hook={DH.SEATING_ACCESSIBILITY_MODE}>
          {!inBasket && !inDescription ? (
            <>
              <div className={classes.backButtonContainer}>
                <TextButton
                  className={classes.backButton}
                  prefixIcon={isRTL ? <ArrowRightSmall /> : <ArrowLeftSmall />}
                  onClick={onBackClick}
                  data-hook={DH.BACK_TO_MAP}
                  hoverStyle={HoverStyles.underline}
                >
                  {t('seatings_backToMap_v2')}
                </TextButton>
              </div>
              <Text className={classes.title} tagName="h2">
                {t('seatingMapModal_listTab_title')}
              </Text>
            </>
          ) : null}
          <Filters />
          {placesCount ? <TicketsCountInfo placesCount={placesCount} /> : null}
          <TicketsList showAccessibilityMode={true} />
        </div>
        <Footer onButtonClick={onFooterButtonClick} />
      </>
    )
  }

  return (
    <SlideInModal
      id="accessibility-mode"
      dataHook={DH.SEATING_ACCESSIBILITY_MODE}
      show={showAccessibilityMode}
      backButtonText={t('seatings_closeTicketList')}
      onBackClick={onBackClick}
    >
      <Header />
      <Filters />
      {placesCount ? <TicketsCountInfo placesCount={placesCount} /> : null}
      <TicketsList setForcedPopoverPlanPlaceId={setForcedPopoverPlanPlaceId} showAccessibilityMode={true} />
      {!chooseSeatMode && !chooseMultipleSeatsMode ? <Footer /> : null}
    </SlideInModal>
  )
}
